import { z } from 'zod';
import { hashCanonicalJson } from '../lib/reportGeneration/reportContractHash';

/** Fixed, source-bound copy; callers cannot substitute a brand or authority claim. */
export const FICTIONAL_DEMONSTRATION_NOTICE = 'FICTIONAL DEMONSTRATION — NOT FOR REAL-WORLD DECISIONS' as const;
export const FICTIONAL_DEMONSTRATION_LIMITATION = 'Illustrative fictional inputs; not an issued study, utility commitment, permit, site clearance or investment recommendation.' as const;

const scenarioId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9-]{2,79}$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const identity = {
  schemaVersion: z.literal(1),
  kind: z.literal('fictional-scenario'),
  scenarioId,
  notice: z.literal(FICTIONAL_DEMONSTRATION_NOTICE),
};

/** Public-safe disclosure. Contains no private owner, document or storage handles. */
export const demonstrationDisclosureV1Schema = z.object(identity).strict();
export type DemonstrationDisclosureV1 = z.infer<typeof demonstrationDisclosureV1Schema>;

/** Created only by the maintained, owner-scoped server intake workflow. */
export const demonstrationSiteProvenanceV1Schema = z.object({
  ...identity,
  ownerUid: z.string().trim().min(1).max(128),
  createdDate: z.string().datetime({ offset: true }),
}).strict();
export type DemonstrationSiteProvenanceV1 = z.infer<typeof demonstrationSiteProvenanceV1Schema>;

export const demonstrationDocumentProvenanceV1Schema = z.object({
  ...identity,
  /** Exact UTF-8 `fullExtractedText ?? textContent` stored in the source JSON; no normalization. */
  bodySha256: sha256,
  /** Original uploaded-file bytes; independent of the extracted-body identity. */
  originalFileSha256: sha256.optional(),
}).strict();
export type DemonstrationDocumentProvenanceV1 = z.infer<typeof demonstrationDocumentProvenanceV1Schema>;

export const demonstrationDocumentManifestRowV1Schema = z.object({
  documentId: z.string().trim().min(1).max(256),
  bodySha256: sha256,
  originalFileSha256: sha256.optional(),
  sourceKind: z.enum(['fictional-scenario', 'public-context']),
}).strict();
export type DemonstrationDocumentManifestRowV1 = z.infer<typeof demonstrationDocumentManifestRowV1Schema>;

/** Exact hydrated evidence subset; SED and report selections are stamped separately. */
export const frozenDemonstrationBasisV1Schema = z.object({
  ...identity,
  ownerUid: z.string().trim().min(1).max(128),
  scope: z.enum(['sed', 'report']),
  documents: z.array(demonstrationDocumentManifestRowV1Schema),
  manifestHash: sha256,
}).strict().superRefine((basis, context) => {
  const ids = basis.documents.map(row => row.documentId);
  if (new Set(ids).size !== ids.length || ids.some((id, index) => id !== [...ids].sort()[index])) {
    context.addIssue({ code: 'custom', path: ['documents'], message: 'Demo manifest requires unique document identities in exact ascending order.' });
  }
  if (basis.manifestHash !== hashCanonicalJson(basis.documents)) {
    context.addIssue({ code: 'custom', path: ['manifestHash'], message: 'Demo manifest contents do not match their canonical hash.' });
  }
});
export type FrozenDemonstrationBasisV1 = z.infer<typeof frozenDemonstrationBasisV1Schema>;
